const SCOPES = 'https://www.googleapis.com/auth/spreadsheets';
const SHEETS_API_BASE = 'https://sheets.googleapis.com/v4';

let tokenClient = null;
let initPromise = null;
let accessToken = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

export function getGoogleSheetsConfig() {
    return {
        apiKey: import.meta.env.VITE_GOOGLE_SHEETS_API_KEY,
        clientId: import.meta.env.VITE_GOOGLE_SHEETS_CLIENT_ID,
    };
}

export function isGoogleSheetsConfigured() {
    return Boolean(getGoogleSheetsConfig().clientId);
}

function formatSheetsApiError(status, body) {
    const apiMessage = body?.error?.message;

    if (status === 403) {
        return new Error(
            apiMessage
                || 'Google Sheets access was denied (403). Enable the Google Sheets API in Google Cloud Console and confirm your OAuth client authorized JavaScript origins include this site.'
        );
    }

    return new Error(apiMessage || `Google Sheets API request failed (${status}).`);
}

async function sheetsRequest(path, options = {}) {
    if (!accessToken) {
        throw new Error('Google Sheets is not authorized yet.');
    }

    const { apiKey } = getGoogleSheetsConfig();
    const url = new URL(`${SHEETS_API_BASE}${path}`);

    if (apiKey) {
        url.searchParams.set('key', apiKey);
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw formatSheetsApiError(response.status, body);
    }

    return body;
}

export async function ensureGoogleSheetsClient() {
    const { clientId } = getGoogleSheetsConfig();

    if (!clientId) {
        throw new Error(
            'GOOGLE_SHEETS_CLIENT_ID is not configured. Create an OAuth Web Client ID in Google Cloud Console (see the Sheets API JavaScript quickstart).'
        );
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        await loadScript('https://accounts.google.com/gsi/client');

        tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: SCOPES,
            callback: () => {},
        });
    })();

    return initPromise;
}

function requestGoogleAccessToken() {
    return new Promise((resolve, reject) => {
        tokenClient.callback = (response) => {
            if (response.error !== undefined) {
                reject(new Error(response.error_description || response.error));
                return;
            }

            accessToken = response.access_token;
            resolve(response);
        };

        tokenClient.requestAccessToken({
            prompt: accessToken ? '' : 'consent',
        });
    });
}

export async function createGoogleSpreadsheet(title, headers, rows) {
    await ensureGoogleSheetsClient();
    await requestGoogleAccessToken();

    const createResponse = await sheetsRequest('/spreadsheets', {
        method: 'POST',
        body: JSON.stringify({
            properties: {
                title,
            },
        }),
    });

    const spreadsheetId = createResponse.spreadsheetId;
    if (!spreadsheetId) {
        throw new Error('Google Sheets API did not return a spreadsheet ID.');
    }

    await sheetsRequest(
        `/spreadsheets/${encodeURIComponent(spreadsheetId)}/values/${encodeURIComponent('Sheet1!A1')}?valueInputOption=RAW`,
        {
            method: 'PUT',
            body: JSON.stringify({
                majorDimension: 'ROWS',
                values: [headers, ...rows],
            }),
        }
    );

    return {
        spreadsheet_id: spreadsheetId,
        url: `https://docs.google.com/spreadsheets/d/${spreadsheetId}`,
    };
}
